import BaseComponent from "./base_component_controller";
import { useOutlets } from "hb/insights/use_outlets";
import { createPopper } from "@popperjs/core";
import URL from "hb/lib/url";
import { safeOpenUrl, validUrl } from "hb/lib/url";
import Temporal from "hb/time/temporal";
import { getTimeFromParam } from "hb/time/time";
import { containsNonIdentChars } from "hb/insights/utils";
import { useListen } from "hb/insights/use_listen";
import { escapeExpression, escapeString } from "hb/lib/utils";

export default class extends BaseComponent {
  static targets = ["dropdown", "template"];

  static values = {
    view: String,
    searchPath: String,
    useParamState: { type: Boolean, default: true },
    opts: Object,
  };

  connect() {
    this.template = this.compileElement(this.templateTarget);
    this.removeDropdown = this.removeDropdown.bind(this);
    this.currentShowing = null;
    useOutlets(this);
    useListen(this);

    this.listenWindow("mousemove", this.trackMousePos);
  }

  trackMousePos(evt) {
    this.mouseX = evt.clientX;
    this.mouseY = evt.clientY;
  }

  show(type, name, value, opts = {}) {
    this.callerEl = opts.element;
    if (this.currentShowing === `${type}-${name}-${value}`) {
      this.handsfreeClose();
      return;
    }

    let queryName = name;
    if (containsNonIdentChars(name) && !name.startsWith("@")) {
      queryName = `\`${name}\``;
    }

    let typedName;
    if (name.startsWith("@") || opts.excludeTypeFromField) {
      typedName = queryName;
    } else {
      typedName = `${queryName}::${type}`;
    }

    const displayName = `<strong>${escapeExpression(name)}</strong>`;
    const displayValue = `<span>${escapeExpression(value)}</span>`;
    const escapedValue = escapeString(value);

    let options = [];

    if (opts.prepend) {
      options = opts.prepend;
    }

    if (name === "request_id" && (type === "string" || type === "str")) {
      options.push({ divider: true, display: "Request ID" });
      options.push({
        op: "replace",
        value: `fields @ts, @preview\n| filter request_id::str == "${escapedValue}"\n| sort @ts`,
        display: `find events with request ID`,
      });
      options.push({ divider: true, display: "General" });
    }

    if (opts.addToField) {
      options.push({
        op: "append",
        value: `fields ${typedName}`,
        display: `add ${displayName} to fields`,
      });
    }

    if (
      type !== "date" &&
      type !== "datetime" &&
      !type.startsWith("tzdatetime")
    ) {
      options.push({
        op: "append",
        value: `stats count() by ${typedName}`,
        display: `count by ${displayName}`,
      });
    }

    if (type.startsWith("tzdatetime") || type === "datetime") {
      const baseDate = Temporal.fromTimestamp(value).inLocalTimezone();

      const buildDate = (
        beforeFn,
        beforeAmt,
        beforeUnit,
        afterFn,
        afterAmt,
        afterUnit,
      ) => {
        let before, after;
        if (beforeFn !== null) {
          before = baseDate.dup()[beforeFn](beforeAmt, beforeUnit).param;
        } else {
          before = baseDate.dup().param;
        }

        if (afterFn !== null) {
          after = baseDate.dup()[afterFn](afterAmt, afterUnit).param;
        } else {
          after = baseDate.dup().param;
        }

        return [before, after].join("/");
      };

      const [startTime, endTime] = getTimeFromParam();

      options.push({
        display: "start at this time",
        scroll: true,
        ts: [baseDate.param, endTime?.param].join("/"),
      });

      options.push({
        display: "end at this time",
        scroll: true,
        ts: [startTime.param, baseDate.param].join("/"),
      });

      options.push({
        display: `around this time`,
        children: [
          {
            display: "Between",
            children: [
              {
                ts: buildDate("subtract", 1, "minute", "add", 1, "minute"),
                display: `-1m → Event time → +1m`,
              },
              {
                ts: buildDate("subtract", 15, "minute", "add", 15, "minute"),
                display: `-15m → Event time → +15m`,
              },
              {
                ts: buildDate("subtract", 1, "hour", "add", 1, "hour"),
                display: `-1h → Event time → +1h`,
              },
            ],
          },
          {
            display: "After",
            children: [
              {
                ts: buildDate(null, null, null, "add", 1, "minute"),
                display: `Event time → +1m`,
              },
              {
                ts: buildDate(null, null, null, "add", 15, "minute"),
                display: `Event time → +15m`,
              },
              {
                ts: buildDate(null, null, null, "add", 1, "hour"),
                display: `Event time → +1h`,
              },
            ],
          },
          {
            display: "Before",
            children: [
              {
                ts: buildDate("subtract", 1, "minute", null, null, null),
                display: `-1m → Event time`,
              },
              {
                ts: buildDate("subtract", 15, "minute", null, null, null),
                display: `-15m → Event time`,
              },
              {
                ts: buildDate("subtract", 1, "hour", null, null, null),
                display: `-1h → Event time`,
              },
            ],
          },
        ],
      });
    }

    if (type === "string" || type === "str") {
      options.push({
        noExternal: true,
        op: "appendPush",
        value: `filter ${typedName} == "???"`,
        display: `${displayName} == <code>?</code>`,
      });
      options.push({
        noExternal: true,
        op: "appendPush",
        value: `parse ${typedName} /???/`,
        display: `parse ${displayName} /<code>?</code>/`,
      });
    }

    if (value === undefined) {
      options.push({
        op: "append",
        value: `filter isNotNull(${typedName})`,
        display: `remove null ${displayName}`,
      });
    } else {
      if (type === "string" || type == "str") {
        options.push({
          op: "append",
          value: `filter ${typedName} == "${escapedValue}"`,
          display: `${displayName} == "${displayValue}"`,
        });
      } else if (type === "integer" || type == "int" || type === "float") {
        options.push({
          op: "append",
          value: `filter ${typedName} == ${value}`,
          display: `${displayName} == ${displayValue}`,
        });
        options.push({
          op: "append",
          value: `filter ${typedName} > ${value}`,
          display: `${displayName} > ${displayValue}`,
        });
        options.push({
          op: "append",
          value: `filter ${typedName} < ${value}`,
          display: `${displayName} < ${displayValue}`,
        });
        options.push({
          op: "append",
          value: `sort ${typedName} desc`,
          display: `sort ${displayName} descending`,
        });
        options.push({
          op: "append",
          value: `sort ${typedName} asc`,
          display: `sort ${displayName} ascending`,
        });
      }
    }

    // Check if value is a valid URL (for string types)
    if ((type === "string" || type === "str") && value !== undefined) {
      if (validUrl(value)) {
        options.push({
          op: "open",
          value: value,
          display: `open URL`,
          newWindow: true,
        });
      }
    }

    options.push({
      noExternal: true,
      value,
      op: "copy",
      display: `copy to clipboard`,
    });

    if (
      this.viewValue === "dashboard" ||
      this.viewValue === "error" ||
      this.viewValue === "site"
    ) {
      options = options.filter((o) => !o.noExternal);
    }

    this.dropdownTarget.innerHTML = this.template({
      options,
      context: opts.context,
    });
    this.dropdownTarget.style.left = `${this.mouseX}px`;
    this.dropdownTarget.style.top = `${this.mouseY}px`;
    this.dropdownTarget.classList.add("show");

    const reference = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: this.mouseY,
        left: this.mouseX,
        right: this.mouseX,
        bottom: this.mouseY,
      }),
      clientWidth: 0,
      clientHeight: 0,
    };

    this.popperInstance = createPopper(reference, this.dropdownTarget, {
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 10],
          },
        },
      ],
    });

    // If we are too close to the right, add a class to flip the dropdown
    if (
      options.some((o) => o.children !== undefined) &&
      window.innerWidth - this.mouseX < 300
    ) {
      this.dropdownTarget.classList.add("flip");
    } else {
      this.dropdownTarget.classList.remove("flip");
    }

    window.removeEventListener("click", this.removeDropdown);
    setTimeout(() => window.addEventListener("click", this.removeDropdown), 0);
    this.currentShowing = `${type}-${name}-${value}`;
  }

  queryUpdate(target) {
    let el = target;
    let newWindow = false;

    if (el.dataset.external === "true") {
      newWindow = true;
      el = el.closest(".handsfree-option");
    }

    const { op, value, ts, scroll, newWindow: optionNewWindow } = el.dataset;

    if (optionNewWindow === "true") {
      newWindow = true;
    }
    let query;
    if (this.useParamStateValue) {
      query = URL.params().get("query");
    } else {
      query = this.callerEl.closest("[data-query]").dataset.query;
    }

    if (scroll === "true") {
      this.callerEl.scrollTo({ top: 0, left: 0 });
    }

    if (op === "copy") {
      navigator.clipboard.writeText(value);
    } else if (newWindow && ts !== "" && ts !== undefined) {
      const params = URL.params();
      params.set("query", query);
      params.set("ts", ts);
      URL.newWindow(params);
    } else if (ts !== "" && ts !== undefined) {
      this.emitWindow("submitQuery", { query, ts });
    } else if (op === "open" && newWindow) {
      safeOpenUrl(value);
    } else if (op === "open") {
      URL.visit(null, value);
    } else if (newWindow && op === "append") {
      URL.updateParamInNewWindow("query", `${query}\n| ${value}`);
    } else if (newWindow && op === "replace") {
      URL.updateParamInNewWindow("query", value);
    } else if (op === "appendPush") {
      window.dispatchEvent(
        new CustomEvent("queryPush", {
          detail: { query: `${query}\n| ${value}` },
        }),
      );
    } else if (op === "replace") {
      this.emitWindow("submitQuery", { query: value });
    } else if (value !== "") {
      this.emitWindow("submitQuery", { query: `${query}\n| ${value}` });
    }
  }

  errorUpdate(target) {
    const { op, value, context } = target.dataset;

    if (op === "copy") {
      navigator.clipboard.writeText(value);
    } else if (op === "open" || op === "errorSearch") {
      safeOpenUrl(value);
    } else {
      const params = URL.params();
      const baseDate = Temporal.fromTimestamp(
        this.optsValue.base_time,
      ).inLocalTimezone();

      const ts = [
        baseDate.dup().subtract(1, "hour").param,
        baseDate.dup().add(1, "hour").param,
      ].join("/");

      let query;
      if (op === "replace") {
        query = value;
      } else {
        query = "fields @ts, @preview\n| sort @ts";
        if (context === "error") {
          query += `\n| filter event_type::str == "notice" and fault_id::int == ${this.optsValue.fault_id}`;
        } else if (context === "all-errors") {
          query += `\n| filter event_type::str == "notice"`;
        }
        query += `\n| ${value}`;
      }

      if (
        (context === "error" || context == "all-errors") &&
        this.optsValue.stream_ids.errors !== undefined
      ) {
        params.set(
          "stream_ids",
          JSON.stringify([this.optsValue.stream_ids.errors]),
        );
      } else if (
        context === "customer" &&
        this.optsValue.stream_ids.customer !== undefined
      ) {
        params.set(
          "stream_ids",
          JSON.stringify([this.optsValue.stream_ids.customer]),
        );
      }

      params.set("query", query);
      params.set("timezone", Temporal.localTimezone().zone);
      params.set("ts", ts);
      URL.newWindow(params, this.searchPathValue);
    }
  }

  siteUpdate(target) {
    const { value, context } = target.dataset;
    const params = URL.params();

    let query =
      "fields @ts, location::str, duration::int, site.name::str, site.url::str, site.state::str\n| sort @ts";
    if (context === "site") {
      query += `\n| filter event_type::str == "uptime_check" and site.id::str == "${this.optsValue.site_id}"`;
    }
    query += `\n| ${value}`;

    params.set("query", query);
    params.set("timezone", Temporal.localTimezone().zone);
    URL.newWindow(params, this.searchPathValue);
  }

  dashboardUpdate(target) {
    const { op, ts, value } = target.dataset;
    const query = this.callerEl.closest("[data-query]").dataset.query;
    if (op === "open") {
      safeOpenUrl(value);
    } else if (op === "copy") {
      navigator.clipboard.writeText(value);
    } else {
      let nextParams = URL.slice(["ts", "timezone"]);

      if (ts !== "" && ts !== undefined) {
        nextParams.set("ts", ts);
      }

      if (op === "replace") {
        nextParams.set("query", value);
      } else if (value === "") {
        nextParams.set("query", query);
      } else {
        nextParams.set("query", `${query}\n| ${value}`);
      }

      URL.newWindow(nextParams, this.searchPathValue);
    }
  }

  handsfreeUpdate({ target }) {
    window.removeEventListener("click", this.removeDropdown);
    this.handsfreeClose();

    if (this.viewValue === "dashboard") {
      this.dashboardUpdate(target);
    } else if (this.viewValue === "error") {
      this.errorUpdate(target);
    } else if (this.viewValue === "site") {
      this.siteUpdate(target);
    } else {
      this.queryUpdate(target);
    }
  }

  handsfreeClose() {
    this.dropdownTarget.classList.remove("show");
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }
    this.currentShowing = null;
  }

  removeDropdown(evt) {
    if (!this.dropdownTarget.contains(evt.target)) {
      window.removeEventListener("click", this.removeDropdown);
      this.handsfreeClose();
    }
  }
}
